import { useMemo, useState } from 'react';
import {
  Search, Euro, Users2, Medal, ArrowUp, ArrowDown, ChevronsUpDown,
  Radio, MapPin, Layers, Sparkles,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { accentForFill } from '@/lib/colors';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

// Non-hero (secondary) columns. Hero columns (Don moyen / Âge moyen) are rendered
// separately with a premium, eye-catching treatment.
const SECONDARY_COLUMNS = [
  { key: 'bs_reel', label: 'BS validés', fmt: (v) => fmtInt(v), strong: true, bar: true },
  { key: 'bs_rue', label: 'BS signés', fmt: (v) => fmtInt(v) },
  { key: 'taux_transfo', label: 'Transfo', fmt: (v) => fmtPct(v) },
  { key: 'productivite', label: 'Prod. (BS/h)', fmt: (v) => fmtNum(v, 2) },
  { key: 'heures', label: 'Heures', fmt: (v) => fmtInt(v) },
  { key: 'jours_actifs', label: 'Jours', fmt: (v) => fmtInt(v) },
];

const PAGE = 15;

// Soft accent divider used to frame the two hero (Don moyen / Âge moyen) columns.
const ACCENT_EDGE = { borderColor: 'var(--tenant-accent-100)' };

// Sort caret shown in a column header.
function SortCaret({ active, dir }) {
  if (!active) return <ChevronsUpDown className="h-3 w-3 opacity-40" />;
  return dir === 'asc'
    ? <ArrowUp className="h-3 w-3 accent-text" />
    : <ArrowDown className="h-3 w-3 accent-text" />;
}

export function CampaignCodeKpiTable({
  items = [],
  showAssociation = false,
  title = 'Détail par code campagne',
  subtitle = 'Âge moyen & don moyen — code par code',
  testid = 'campaign-code-kpi-table',
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('bs_reel');
  const [sortDir, setSortDir] = useState('desc');
  const [limit, setLimit] = useState(PAGE);

  // Scale references for the micro-bars (computed once over the full set so the
  // visual scale stays stable while searching / paginating).
  const scale = useMemo(() => {
    const dons = items.map((i) => i.don_moyen || 0);
    const ages = items.map((i) => i.age_moyen || 0).filter((a) => a > 0);
    const bs = items.map((i) => i.bs_reel || 0);
    return {
      donMax: Math.max(1, ...dons),
      ageMin: ages.length ? Math.min(...ages) : 0,
      ageMax: ages.length ? Math.max(...ages) : 1,
      bsMax: Math.max(1, ...bs),
    };
  }, [items]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'key' ? 'asc' : 'desc');
    }
    setLimit(PAGE);
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? items.filter((i) =>
          [i.key, i.label, i.region, i.canal, i.association]
            .filter(Boolean)
            .some((s) => String(s).toLowerCase().includes(q)))
      : items.slice();
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === 'key') return String(a.key).localeCompare(String(b.key)) * dir;
      return ((a[sortKey] ?? 0) - (b[sortKey] ?? 0)) * dir;
    });
    return list;
  }, [items, search, sortKey, sortDir]);

  const shown = rows.slice(0, limit);

  const ageBar = (age) => {
    if (!age || scale.ageMax === scale.ageMin) return 0;
    const p = (age - scale.ageMin) / (scale.ageMax - scale.ageMin);
    return Math.max(8, Math.round(p * 100));
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border bg-card card-shadow-lg"
      data-testid={testid}>
      {/* Decorative accent band — signals this block is special */}
      <div className="h-1 w-full" style={{ background: 'var(--tenant-gradient)' }} />

      {/* Header */}
      <div className="flex flex-col gap-4 px-5 pt-5 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl accent-bg-soft"
            aria-hidden="true">
            <Layers className="h-5 w-5 accent-text" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-base font-semibold tracking-tight text-foreground">{title}</h3>
              <span className="inline-flex items-center gap-1 rounded-full accent-bg-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide accent-text">
                <Sparkles className="h-3 w-3" /> Focus qualité
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>

        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }}
            placeholder="Rechercher un code, région, canal…"
            className="h-10 rounded-xl pl-9"
            data-testid={`${testid}-search`}
          />
        </div>
      </div>

      {/* Legend for the two hero metrics */}
      <div className="mt-4 flex flex-wrap items-center gap-2 px-5">
        <span className="inline-flex items-center gap-1.5 rounded-lg border accent-bg-soft px-2.5 py-1 text-[11px] font-medium accent-text" style={ACCENT_EDGE}>
          <Euro className="h-3.5 w-3.5" /> Don moyen
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg border accent-bg-soft px-2.5 py-1 text-[11px] font-medium accent-text" style={ACCENT_EDGE}>
          <Users2 className="h-3.5 w-3.5" /> Âge moyen
        </span>
        <span className="text-[11px] text-muted-foreground">mis en avant · triez en cliquant sur les en-têtes</span>
      </div>

      {/* Table */}
      <div className="mt-3 px-2 pb-1">
        {rows.length === 0 ? (
          <div className="px-3 py-12 text-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
            Aucun code campagne ne correspond à votre recherche.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid={`${testid}-table`}>
              <TableHeader>
                <TableRow className="border-b-0 hover:bg-transparent [&>th]:whitespace-nowrap">
                  <TableHead className="w-10 text-muted-foreground">#</TableHead>
                  <SortableHead
                    label="Code campagne" col="key" sortKey={sortKey} sortDir={sortDir}
                    onSort={handleSort} align="left" className="min-w-[190px]" testid={testid} />
                  {/* Hero: Don moyen */}
                  <SortableHead
                    label="Don moyen" col="don_moyen" sortKey={sortKey} sortDir={sortDir}
                    onSort={handleSort} hero icon={Euro} testid={testid} className="min-w-[130px]" />
                  {/* Hero: Âge moyen */}
                  <SortableHead
                    label="Âge moyen" col="age_moyen" sortKey={sortKey} sortDir={sortDir}
                    onSort={handleSort} hero icon={Users2} testid={testid} className="min-w-[130px]" />
                  {SECONDARY_COLUMNS.map((c) => (
                    <SortableHead
                      key={c.key} label={c.label} col={c.key} sortKey={sortKey} sortDir={sortDir}
                      onSort={handleSort} testid={testid} />
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((r, i) => {
                  const rank = i + 1;
                  const isTop = sortKey === 'bs_reel' && sortDir === 'desc' && rank <= 3;
                  return (
                    <TableRow
                      key={`${r.key}-${r.association || ''}`}
                      className="group border-border/60"
                      data-testid={`${testid}-row`}>
                      {/* Rank */}
                      <TableCell className="align-top">
                        {isTop ? (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full accent-bg-soft"
                            title={`Top ${rank}`}>
                            <Medal className="h-3.5 w-3.5 accent-text" />
                          </span>
                        ) : (
                          <span className="pl-1.5 text-xs tabular text-muted-foreground">{rank}</span>
                        )}
                      </TableCell>

                      {/* Code + context */}
                      <TableCell className="align-top">
                        <div className="font-mono text-sm font-semibold tracking-tight text-foreground">{r.key}</div>
                        {r.label && (
                          <div className="mt-0.5 max-w-[220px] truncate text-[11px] text-muted-foreground" title={r.label}>
                            {r.label}
                          </div>
                        )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {showAssociation && r.association && (
                            <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium text-foreground">
                              <span className="h-2 w-2 rounded-full" style={{ background: accentForFill(r.color || '#EE722E') }} />
                              {r.association}
                            </span>
                          )}
                          {r.canal && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                              <Radio className="h-2.5 w-2.5" />{r.canal}
                            </span>
                          )}
                          {r.region && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                              <MapPin className="h-2.5 w-2.5" />{r.region}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Hero: Don moyen */}
                      <TableCell className="border-l accent-bg-soft align-top" style={ACCENT_EDGE} data-testid={`${testid}-don`}>
                        <div className="text-right">
                          <span className="font-display text-lg font-semibold tabular accent-text">{fmtEur(r.don_moyen, 2)}</span>
                        </div>
                        <MicroBar pct={Math.max(6, Math.round(((r.don_moyen || 0) / scale.donMax) * 100))} />
                      </TableCell>

                      {/* Hero: Âge moyen */}
                      <TableCell className="border-r accent-bg-soft align-top" style={ACCENT_EDGE} data-testid={`${testid}-age`}>
                        <div className="text-right">
                          <span className="font-display text-lg font-semibold tabular accent-text">{fmtNum(r.age_moyen, 1)}</span>
                          <span className="ml-1 text-[11px] text-muted-foreground">ans</span>
                        </div>
                        <MicroBar pct={ageBar(r.age_moyen)} />
                      </TableCell>

                      {/* Secondary metrics */}
                      {SECONDARY_COLUMNS.map((c) => (
                        <TableCell
                          key={c.key}
                          className={`text-right align-top tabular whitespace-nowrap ${c.strong ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                          {c.fmt(r[c.key])}
                          {c.bar && (
                            <MicroBar pct={Math.max(4, Math.round(((r.bs_reel || 0) / scale.bsMax) * 100))} neutral />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col items-center justify-between gap-3 border-t px-5 py-3 sm:flex-row">
        <p className="text-xs text-muted-foreground" data-testid={`${testid}-count`}>
          {shown.length} / {rows.length} code{rows.length > 1 ? 's' : ''} campagne affiché{shown.length > 1 ? 's' : ''}
        </p>
        {shown.length < rows.length && (
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={() => setLimit((l) => l + PAGE)}
            data-testid={`${testid}-load-more`}>
            Afficher plus ({rows.length - shown.length})
          </Button>
        )}
      </div>
    </div>
  );
}

// Clickable, sortable column header. `hero` gives the premium tinted treatment.
function SortableHead({ label, col, sortKey, sortDir, onSort, align = 'right', hero = false, icon: Icon, className = '', testid }) {
  const active = sortKey === col;
  return (
    <TableHead
      style={hero ? ACCENT_EDGE : undefined}
      className={`${hero ? 'accent-bg-soft border-x' : ''} ${align === 'left' ? 'text-left' : 'text-right'} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        aria-pressed={active}
        data-testid={`${testid}-sort-${col}`}
        className={`inline-flex w-full items-center gap-1 rounded-md px-1 py-0.5 text-xs font-semibold transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${align === 'left' ? 'justify-start' : 'justify-end'} ${hero || active ? 'accent-text' : 'text-muted-foreground'}`}>
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <span>{label}</span>
        <SortCaret active={active} dir={sortDir} />
      </button>
    </TableHead>
  );
}

// Thin proportional bar used under KPI values to add relief / scannability.
function MicroBar({ pct = 0, neutral = false }) {
  return (
    <div className="mt-1 ml-auto h-1 w-full max-w-[72px] overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full"
        style={{
          width: `${Math.min(100, Math.max(0, pct))}%`,
          background: neutral ? 'hsl(var(--muted-foreground) / 0.45)' : 'var(--tenant-accent-500)',
        }}
      />
    </div>
  );
}
